import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const ServiceOrderCard = ({ 
  order, 
  onStatusUpdate, 
  onStartTimer, 
  onPauseTimer, 
  onCompleteOrder,
  onContactClient,
  onUploadPhoto,
  isTimerActive = false 
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'alta':
        return 'bg-error text-error-foreground';
      case 'media':
        return 'bg-warning text-warning-foreground';
      case 'baixa':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'em_andamento':
        return 'bg-primary text-primary-foreground';
      case 'pausada':
        return 'bg-warning text-warning-foreground';
      case 'concluida':
        return 'bg-success text-success-foreground';
      case 'pendente':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'em_andamento':
        return 'Em Andamento';
      case 'pausada':
        return 'Pausada';
      case 'concluida':
        return 'Concluída';
      case 'pendente':
        return 'Pendente';
      default:
        return 'Não Iniciada';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional mb-4 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="font-semibold text-card-foreground text-lg">
                OS #{order?.id}
              </h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(order?.priority)}`}>
                {order?.priority?.charAt(0)?.toUpperCase() + order?.priority?.slice(1)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-1">
              {order?.serviceType}
            </p>
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>{order?.location}</span>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-2">
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(order?.status)}`}>
              {getStatusLabel(order?.status)}
            </span>
            {order?.estimatedValue && (
              <span className="text-sm font-medium text-accent">
                {formatCurrency(order?.estimatedValue)}
              </span>
            )}
          </div>
        </div>

        {/* Client Info */}
        <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="User" size={18} color="white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-card-foreground truncate">
              {order?.clientName}
            </p>
            <p className="text-sm text-muted-foreground truncate">
              {order?.clientPhone}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onContactClient(order)}
            className="flex-shrink-0"
          >
            <Icon name="Phone" size={18} />
          </Button>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          {order?.status === 'pendente' && (
            <Button
              variant="default"
              onClick={() => onStartTimer(order)}
              className="touch-target"
              iconName="Play"
              iconPosition="left"
            >
              Iniciar
            </Button>
          )}
          
          {order?.status === 'em_andamento' && (
            <Button
              variant="warning"
              onClick={() => onPauseTimer(order)}
              className="touch-target"
              iconName="Pause"
              iconPosition="left"
            >
              Pausar
            </Button>
          )}
          
          {order?.status === 'pausada' && (
            <Button
              variant="default"
              onClick={() => onStartTimer(order)}
              className="touch-target"
              iconName="Play"
              iconPosition="left"
            >
              Retomar
            </Button>
          )}

          {(order?.status === 'em_andamento' || order?.status === 'pausada') && (
            <Button
              variant="success"
              onClick={() => onCompleteOrder(order)}
              className="touch-target"
              iconName="CheckCircle"
              iconPosition="left"
            >
              Concluir
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => onUploadPhoto(order)}
            className="touch-target"
            iconName="Camera"
            iconPosition="left"
          >
            Foto
          </Button>

          <Button
            variant="ghost"
            onClick={() => setShowDetails(!showDetails)}
            className="touch-target"
            iconName={showDetails ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            Detalhes
          </Button>
        </div>

        {/* Timer Display */}
        {isTimerActive && order?.status === 'em_andamento' && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-success">
                  Tempo ativo
                </span>
              </div>
              <span className="font-mono text-lg font-semibold text-success">
                {formatTime(order?.elapsedTime || 0)}
              </span>
            </div>
          </div>
        )}

        {/* Expanded Details */}
        {showDetails && (
          <div className="space-y-4 pt-4 border-t border-border">
            <div>
              <h4 className="font-medium text-card-foreground mb-2">
                Descrição do Serviço
              </h4>
              <p className="text-sm text-muted-foreground">
                {order?.description}
              </p>
            </div>

            {order?.scheduledDate && (
              <div>
                <h4 className="font-medium text-card-foreground mb-2">
                  Data Agendada
                </h4>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>{order?.scheduledDate}</span>
                  <Icon name="Clock" size={14} />
                  <span>{order?.scheduledTime}</span>
                </div>
              </div>
            )}

            {order?.equipment && order?.equipment?.length > 0 && (
              <div>
                <h4 className="font-medium text-card-foreground mb-2">
                  Equipamentos
                </h4>
                <div className="space-y-2">
                  {order?.equipment?.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <Icon name="Tool" size={14} className="text-muted-foreground" />
                      <span className="text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {order?.notes && (
              <div>
                <h4 className="font-medium text-card-foreground mb-2">
                  Observações
                </h4>
                <p className="text-sm text-muted-foreground">
                  {order?.notes}
                </p>
              </div>
            )}

            {/* Location Map */}
            <div>
              <h4 className="font-medium text-card-foreground mb-2">
                Localização
              </h4>
              <div className="w-full h-32 bg-muted rounded-lg overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`Localização - ${order?.clientName}`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${order?.coordinates?.lat || -23.5505},${order?.coordinates?.lng || -46.6333}&z=14&output=embed`}
                  className="border-0"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceOrderCard;